import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const outcomeLabel = {
  interested: 'Interested',
  not_interested: 'Not Interested',
  call_later: 'Call Later',
  no_answer: 'No Answer'
};

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [viewCallModalOpen, setViewCallModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailsFollowUps, setDetailsFollowUps] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callForm, setCallForm] = useState({
    id: '',
    name: '',
    phone: '',
    email: '',
    businessName: '',
    source: 'user',
    callType: 'outgoing',
    callStatus: '',
    note: '',
    nextFollowDateTime: '',
    followUpStatus: 'pending',
    followUpOutcome: 'call_later'
  });
  const [followUpForm, setFollowUpForm] = useState({
    callId: '',
    status: 'pending',
    outcome: '',
    note: '',
    callStatus: 'callback',
    nextFollowDateTime: ''
  });
  const [associationModal, setAssociationModal] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletedFilter, setDeletedFilter] = useState('0');

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedUserForImage, setSelectedUserForImage] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        date: startDate && endDate ? `${startDate} - ${endDate}` : '',
        isDeletedFilter: deletedFilter
      };
      
      const response = await apiService.post('users', params);
      if (response && response.data) {
        setUsers(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Users fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, startDate, endDate, deletedFilter]);

  const checkUserAssociations = async (id, name) => {
    try {
      const response = await apiService.get(`checkUserAssociations/${id}`);
      setAssociationModal({ id, name, response });
    } catch (error) {
      toast.error('Error checking user associations');
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      const response = await apiService.put(`restoreUser/${userId}`);
      if (response.status || response.success) {
        toast.success('User restored successfully');
        fetchUsers();
      } else {
        toast.error(response.message || 'Failed to restore user');
      }
    } catch (error) {
      toast.error('Error restoring user');
    }
  };

  const handleDeleteUser = async () => {
    setDeleteLoading(true);
    try {
      const response = await apiService.delete(`deleteUser/${associationModal.id}`);
      if (response.success || response.status) {
        toast.success('User deleted successfully');
        setAssociationModal(null);
        fetchUsers();
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Error deleting user');
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKeyword(value);
      setCurrentPage(1);
    }, 500);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setShowDatePicker(false);
  };

  const toggleUserStatus = async (userId) => {
    try {
      const response = await apiService.get(`userStatus/${userId}`);
      if (response.status) {
        toast.success(response.message || 'User status updated');
        // Update user in state instead of refetching all
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u._id === userId ? { ...u, block: u.block === 1 ? 0 : 1 } : u
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const toggleTestingStatus = async (userId) => {
    try {
      const response = await apiService.get(`vendorStatusTesting/${userId}`);
      if (response.status) {
        toast.success('User testing status changed!');
        // Update user in state instead of refetching all
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u._id === userId ? { ...u, isTesting: u.isTesting === 1 ? 0 : 1 } : u
          )
        );
      }
    } catch (error) {
      toast.error('Failed to update testing status');
    }
  };

  const openImageModal = (userData) => {
    setSelectedUserForImage(userData);
    setImagePreview(userData.image ? `${process.env.REACT_APP_MEDIA_URL}${userData.image}` : null);
    setNewImageFile(null);
    setImageModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!newImageFile) {
      toast.error("Please select an image");
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', newImageFile);
      const response = await apiService.postFormData(`updateUserImage/${selectedUserForImage._id}`, formData);
      if (response.status) {
        toast.success(response.message || 'Image updated successfully');
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u._id === selectedUserForImage._id ? { ...u, image: response.data.image } : u
          )
        );
        setImageModalOpen(false);
      } else {
        toast.error(response.message || 'Failed to update image');
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '--';
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

  const buildCallPayloadFromUser = (userData) => ({
    name: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || userData?.first_name || 'User',
    phone: userData?.phone || '',
    email: userData?.email || '',
    businessName: '',
    source: 'user',
    callType: 'outgoing',
    callStatus: '',
    duration: undefined,
    note: ''
  });

  const findCallByPhone = async (phone) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return null;
    const response = await apiService.post('call/list', { start: 0, length: 50, search: normalized });
    const rows = Array.isArray(response?.data) ? response.data : [];
    return rows.find((row) => normalizePhone(row?.contactId?.phone) === normalized) || null;
  };

  const loadCallFollowUps = async (callId) => {
    const response = await apiService.post('call/followup/list', { start: 0, length: 200 });
    const rows = Array.isArray(response?.data) ? response.data : [];
    return rows.filter((row) => row?.callId?._id === callId);
  };

  const openCreateCallModal = async (userData) => {
    try {
      const payload = buildCallPayloadFromUser(userData);
      if (!payload.phone) {
        toast.error('User phone is required');
        return;
      }
      const existingCall = await findCallByPhone(payload.phone);
      setCallForm({
        id: existingCall?._id || '',
        name: payload.name,
        phone: payload.phone,
        email: payload.email,
        businessName: '',
        source: 'user',
        callType: existingCall?.callType || 'outgoing',
        callStatus: existingCall?.lastFollowUp?.callStatus || '',
        note: existingCall?.note || '',
        nextFollowDateTime: existingCall?.lastFollowUp?.nextFollowDateTime || '',
        followUpStatus: existingCall?.lastFollowUp?.status || 'pending',
        followUpOutcome: existingCall?.lastFollowUp?.outcome || 'call_later'
      });
      setCallModalOpen(true);
    } catch (error) {
      toast.error('Failed to load call data');
    }
  };

  const saveCall = async (e) => {
    e.preventDefault();
    try {
      setCallSubmitting(true);
      const isCreate = !callForm.id;
      if (isCreate && !callForm.nextFollowDateTime) {
        toast.error('Follow-up date and time is required');
        return;
      }

      const payload = {
        name: callForm.name,
        phone: callForm.phone,
        email: callForm.email,
        businessName: '',
        source: 'user',
        callType: callForm.callType,
        note: callForm.note
      };

      if (isCreate) {
        payload.nextFollowDateTime = callForm.nextFollowDateTime;
        payload.status = callForm.followUpStatus;
        payload.outcome = callForm.followUpOutcome ? callForm.followUpOutcome : undefined;
        payload.callStatus = callForm.callStatus || undefined;
      }

      if (callForm.id) {
        await apiService.put(`call/update/${callForm.id}`, payload);
        toast.success('Call updated successfully');
      } else {
        await apiService.post('call/create', payload);
        toast.success('Call created successfully');
      }
      setCallModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save call');
    } finally {
      setCallSubmitting(false);
    }
  };

  const openFollowUpModal = async (userData) => {
    try {
      const existingCall = await findCallByPhone(userData?.phone);
      if (!existingCall?._id) {
        toast.error('Create call first for this user');
        return;
      }
      setFollowUpForm({
        callId: existingCall._id,
        status: 'pending',
        outcome: '',
        note: '',
        callStatus: 'callback',
        nextFollowDateTime: ''
      });
      setFollowUpModalOpen(true);
    } catch (error) {
      toast.error('Failed to open follow-up');
    }
  };

  const saveFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.callId || !followUpForm.nextFollowDateTime) {
      toast.error('Follow-up date and time is required');
      return;
    }
    try {
      await apiService.post('call/followup/create', followUpForm);
      toast.success('Follow-up created successfully');
      setFollowUpModalOpen(false);
      if (selectedCall?._id) {
        const updated = await loadCallFollowUps(selectedCall._id);
        setDetailsFollowUps(updated);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create follow-up');
    }
  };

  const openViewCallModal = async (userData) => {
    try {
      setDetailsLoading(true);
      const existingCall = await findCallByPhone(userData?.phone);
      if (!existingCall?._id) {
        toast.error('No call found for this user');
        setDetailsLoading(false);
        return;
      }
      setSelectedCall(existingCall);
      setViewCallModalOpen(true);
      const followUps = await loadCallFollowUps(existingCall._id);
      setDetailsFollowUps(followUps);
    } catch (error) {
      toast.error('Failed to load call details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-0">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Filters Bar */}
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Type Name, Phone or Email"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent text-sm"
              />
            </div>
            <div className="relative">
              <div 
                className="flex items-center gap-2 px-2 sm:px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm min-w-[200px]"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <input
                  type="text"
                  placeholder="Pick date range"
                  value={startDate && endDate ? `${startDate} - ${endDate}` : ''}
                  readOnly
                  className="flex-1 bg-transparent border-none outline-none cursor-pointer text-sm"
                />
                <i className="bi bi-calendar3 text-gray-400"></i>
              </div>
              {showDatePicker && (
                <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10 min-w-[300px]">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">From:</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">To:</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button 
                      type="button" 
                      onClick={clearDateRange} 
                      className="flex-1 px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Clear
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleDateChange(startDate, endDate)}
                      disabled={!startDate || !endDate}
                      className="flex-1 px-2 sm:px-4 py-2 text-sm font-medium text-white bg-[#009ef7] rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
            <select
              value={deletedFilter}
              onChange={(e) => { setDeletedFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7] focus:border-transparent bg-white text-gray-700 font-medium"
            >
              <option value="0">Active Users</option>
              <option value="1">Deleted Users</option>
              <option value="All">All Users</option>
            </select>
          </div>

          {/* Users Table */}
          <div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
                <p className="mt-4 text-gray-600">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <i className="bi bi-people text-6xl text-gray-300"></i>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">No Users Found</h3>
                <p className="mt-2 text-sm text-gray-600">No users match your current search criteria.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                      <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added On</th>
                      {user?.role === 'admin' && (
                        <>
                          <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Call Actions</th>
                          <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Testing</th>
                          <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Blocked</th>
                          <th className="px-2 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((userData, index) => (
                      <tr key={userData._id} className="hover:bg-gray-50">
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {(currentPage - 1) * recordsPerPage + index + 1}
                        </td>
                        <td className="px-2 sm:px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div 
                              onClick={() => openImageModal(userData)}
                              className="group relative w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
                              title="Click to edit image"
                            >
                              {userData.image ? (
                                <img
                                  src={`${process.env.REACT_APP_MEDIA_URL}${userData.image}`}
                                  alt={userData.first_name}
                                  className="w-full h-full object-cover group-hover:opacity-40 transition-opacity"
                                />
                              ) : (
                                <i className="bi bi-person-fill text-gray-400 text-xl group-hover:opacity-40 transition-opacity"></i>
                              )}
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="bi bi-camera-fill text-white text-xs"></i>
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 whitespace-nowrap">{userData.first_name}</div>
                              <div className="text-xs text-gray-500 whitespace-nowrap">#{userData._id.slice(-6)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-4">
                          <div className="text-sm text-gray-900 whitespace-nowrap">{userData.email}</div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500 whitespace-nowrap">{userData.phone || '-'}</span>
                            {userData.phone && (
                              <a
                                href={`tel:${userData.phone}`}
                                className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors"
                                title={`Call ${userData.phone}`}
                              >
                                <i className="bi bi-telephone-fill text-[9px]"></i>
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {userData.countryId?.name || '--'}
                        </td>
                        <td className="px-2 sm:px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {formatDate(userData.createdAt)}
                        </td>
                        {user?.role === 'admin' && (
                          <>
                            <td className="px-2 sm:px-4 py-4 text-center">
                              <div className="flex flex-col gap-1 items-center">
                                <button
                                  type="button"
                                  onClick={() => openCreateCallModal(userData)}
                                  className="inline-block px-2 py-1 text-[11px] font-semibold bg-indigo-100 text-indigo-800 rounded-md hover:bg-indigo-200"
                                >
                                  <i className="bi bi-telephone-plus mr-1"></i>Create Call
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openFollowUpModal(userData)}
                                  className="inline-block px-2 py-1 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-md hover:bg-amber-200"
                                >
                                  <i className="bi bi-calendar-check mr-1"></i>Follow-up
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openViewCallModal(userData)}
                                  className="inline-block px-2 py-1 text-[11px] font-semibold bg-teal-100 text-teal-800 rounded-md hover:bg-teal-200"
                                >
                                  <i className="bi bi-eye mr-1"></i>View Call
                                </button>
                              </div>
                            </td>
                            <td className="px-2 sm:px-4 py-4 text-center">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={userData.isTesting === 1}
                                  onChange={() => toggleTestingStatus(userData._id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                              </label>
                            </td>
                            <td className="px-2 sm:px-4 py-4 text-center">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={userData.block === 1}
                                  onChange={() => toggleUserStatus(userData._id)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                              </label>
                            </td>
                            <td className="px-2 sm:px-4 py-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Link
                                  to={`/view-profile/${userData._id}`}
                                  className="inline-flex items-center justify-center w-8 h-8 text-[#009ef7] hover:bg-[#009ef7]/10 rounded-lg transition-colors"
                                  title="View Profile"
                                >
                                  <i className="bi bi-eye text-lg"></i>
                                </Link>

                                {userData.isDeleted ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRestoreUser(userData._id)}
                                    className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-md hover:bg-emerald-200 transition-colors"
                                    title="Restore User"
                                  >
                                    <i className="bi bi-arrow-counterclockwise mr-1"></i>Restore
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => checkUserAssociations(userData._id, `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'User')}
                                    className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete User"
                                  >
                                    <i className="bi bi-trash text-lg"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {users.length > 0 && (
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs sm:text-sm text-gray-700">Show</span>
                <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700">
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs sm:text-sm text-gray-700">entries</span>
                <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords})</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&laquo;</button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>{page}</button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&raquo;</button>
              </div>
            </div>
          )}        </div>
      </div>

      {callModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{callForm.id ? "Edit Call" : "Create Call"}</h3>
                <p className="text-[11px] text-slate-500">Add call details and the next follow-up</p>
              </div>
              <button type="button" onClick={() => setCallModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={saveCall} className="px-4 py-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" required placeholder="Name" value={callForm.name} onChange={(e) => setCallForm((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                <input type="text" placeholder="Business Name" value={callForm.businessName} onChange={(e) => setCallForm((p) => ({ ...p, businessName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" required placeholder="Phone" value={callForm.phone} onChange={(e) => setCallForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
                <input type="email" placeholder="Email" value={callForm.email} onChange={(e) => setCallForm((p) => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30" />
              </div>
              <select value={callForm.source} onChange={(e) => setCallForm((p) => ({ ...p, source: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="user">User</option>
                <option value="manual">Manual</option>
                <option value="vendor">Vendor</option>
                <option value="lead">Lead</option>
              </select>
              <select value={callForm.callType} onChange={(e) => setCallForm((p) => ({ ...p, callType: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option value="outgoing">Outgoing</option>
                <option value="incoming">Incoming</option>
              </select>
              {!callForm.id && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 space-y-2">
                  <p className="text-[11px] font-semibold text-slate-600">Follow-up Details</p>
                  <input type="datetime-local" required value={callForm.nextFollowDateTime} onChange={(e) => setCallForm((p) => ({ ...p, nextFollowDateTime: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select value={callForm.callStatus} onChange={(e) => setCallForm((p) => ({ ...p, callStatus: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                      <option value="">Call Status</option>
                      <option value="answered">Answered</option>
                      <option value="missed">Missed</option>
                      <option value="busy">Busy</option>
                      <option value="not_connected">Not Connected</option>
                      <option value="invalid">Invalid</option>
                      <option value="disconnected">Disconnected</option>
                      <option value="callback">Callback</option>
                    </select>
                    <select value={callForm.followUpStatus} onChange={(e) => setCallForm((p) => ({ ...p, followUpStatus: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="missed">Missed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <select value={callForm.followUpOutcome} onChange={(e) => setCallForm((p) => ({ ...p, followUpOutcome: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    <option value="">Select Outcome</option>
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="call_later">Call Later</option>
                    <option value="no_answer">No Answer</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3">
                <textarea rows="2" placeholder="Call Note" value={callForm.note} onChange={(e) => setCallForm((p) => ({ ...p, note: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setCallModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={callSubmitting} className="px-3 py-2 text-sm bg-[#009ef7] text-white rounded-lg disabled:opacity-60">
                  {callSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {followUpModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Create Follow-up</h3>
                <p className="text-[11px] text-slate-500">Schedule a follow-up call for this user</p>
              </div>
              <button type="button" onClick={() => setFollowUpModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={saveFollowUp} className="px-4 py-3 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Next Follow-up Date & Time</label>
                <input 
                  type="datetime-local" 
                  required 
                  value={followUpForm.nextFollowDateTime} 
                  onChange={(e) => setFollowUpForm((p) => ({ ...p, nextFollowDateTime: e.target.value }))} 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30 bg-white" 
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Call Status</label>
                  <select 
                    value={followUpForm.callStatus} 
                    onChange={(e) => setFollowUpForm((p) => ({ ...p, callStatus: e.target.value }))} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30"
                  >
                    <option value="">Call Status</option>
                    <option value="answered">Answered</option>
                    <option value="missed">Missed</option>
                    <option value="busy">Busy</option>
                    <option value="not_connected">Not Connected</option>
                    <option value="invalid">Invalid</option>
                    <option value="disconnected">Disconnected</option>
                    <option value="callback">Callback</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Follow-up Status</label>
                  <select 
                    value={followUpForm.status} 
                    onChange={(e) => setFollowUpForm((p) => ({ ...p, status: e.target.value }))} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30"
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="missed">Missed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Note</label>
                <textarea 
                  rows="3" 
                  placeholder="Add follow-up notes..." 
                  value={followUpForm.note} 
                  onChange={(e) => setFollowUpForm((p) => ({ ...p, note: e.target.value }))} 
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/30"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setFollowUpModalOpen(false)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                  Create Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewCallModalOpen && selectedCall && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-sky-50/40">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Call Details & History</h3>
                <p className="text-sm text-slate-500 mt-1">
                  <i className="bi bi-person-circle mr-1"></i>
                  {selectedCall?.contactId?.name || '--'} | 
                  <i className="bi bi-telephone ml-2 mr-1"></i>
                  {selectedCall?.contactId?.phone || '--'}
                </p>
              </div>
              <button 
                onClick={() => setViewCallModalOpen(false)} 
                className="h-10 w-10 rounded-full border border-slate-200 text-slate-500 hover:bg-white hover:shadow-sm transition-all"
              >
                <i className="bi bi-x text-lg"></i>
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="p-6">
                {/* Call Overview Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-blue-50 to-blue-100/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="bi bi-telephone text-blue-600"></i>
                      <p className="text-xs font-semibold text-blue-700">Call Type</p>
                    </div>
                    <p className="text-sm font-bold text-blue-900 capitalize">{selectedCall?.callType || '--'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-green-50 to-green-100/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="bi bi-check-circle text-green-600"></i>
                      <p className="text-xs font-semibold text-green-700">Call Status</p>
                    </div>
                    <p className="text-sm font-bold text-green-900 capitalize">{selectedCall?.callStatus?.replaceAll('_', ' ') || '--'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-purple-50 to-purple-100/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="bi bi-clock text-purple-600"></i>
                      <p className="text-xs font-semibold text-purple-700">Duration</p>
                    </div>
                    <p className="text-sm font-bold text-purple-900">{selectedCall?.duration ? `${selectedCall.duration}s` : '--'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="bi bi-calendar-plus text-orange-600"></i>
                      <p className="text-xs font-semibold text-orange-700">Created</p>
                    </div>
                    <p className="text-sm font-bold text-orange-900">{formatDateTime(selectedCall?.createdAt)}</p>
                  </div>
                </div>

                {/* Call Note Section */}
                <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="bi bi-chat-left-text text-slate-600"></i>
                    <h4 className="text-sm font-semibold text-slate-800">Call Notes</h4>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-slate-100">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {selectedCall?.note || 'No notes available for this call.'}
                    </p>
                  </div>
                </div>

                {/* Follow-up History Section */}
                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-sky-50/40 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="bi bi-calendar-check text-slate-600"></i>
                      <h4 className="text-sm font-semibold text-slate-800">Follow-up History</h4>
                      <span className="px-2 py-1 bg-slate-200 text-slate-700 text-xs font-semibold rounded-full">
                        {detailsFollowUps.length}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setFollowUpForm({ callId: selectedCall?._id, status: 'pending', outcome: '', note: '', callStatus: 'callback', nextFollowDateTime: '' });
                        setFollowUpModalOpen(true);
                      }}
                      className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200 transition-colors flex items-center gap-1"
                    >
                      <i className="bi bi-plus-circle"></i>
                      Add Follow-up
                    </button>
                  </div>
                  
                  {detailsLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto mb-3"></div>
                      <p className="text-sm text-slate-500">Loading follow-up history...</p>
                    </div>
                  ) : detailsFollowUps.length === 0 ? (
                    <div className="p-8 text-center">
                      <i className="bi bi-calendar-x text-4xl text-slate-300 mb-3"></i>
                      <h5 className="text-sm font-semibold text-slate-600 mb-1">No Follow-ups Yet</h5>
                      <p className="text-xs text-slate-500">Create the first follow-up to start tracking this call.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                          <tr>
                            <th className="px-6 py-3 font-semibold">Date & Time</th>
                            <th className="px-6 py-3 font-semibold">Status</th>
                            <th className="px-6 py-3 font-semibold">Outcome</th>
                            <th className="px-6 py-3 font-semibold">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {detailsFollowUps.map((item, index) => (
                            <tr key={item._id} className={`hover:bg-slate-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <i className="bi bi-calendar3 text-slate-400"></i>
                                  <div>
                                    <div className="font-medium text-slate-900">
                                      {formatDateTime(item?.followUpDateTime || item?.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  item?.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  item?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  item?.status === 'missed' ? 'bg-red-100 text-red-800' :
                                  item?.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  'bg-slate-100 text-slate-800'
                                }`}>
                                  {item?.status || '--'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  item?.outcome === 'interested' ? 'bg-green-100 text-green-800' :
                                  item?.outcome === 'not_interested' ? 'bg-red-100 text-red-800' :
                                  item?.outcome === 'call_later' ? 'bg-blue-100 text-blue-800' :
                                  item?.outcome === 'no_answer' ? 'bg-gray-100 text-gray-800' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {item?.outcome ? (outcomeLabel[item.outcome] || item.outcome.replaceAll('_', ' ')) : '--'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="max-w-xs">
                                  <p className="text-sm text-slate-700 truncate" title={item?.note || ''}>
                                    {item?.note || 'No notes'}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {associationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-base font-semibold">Delete User: {associationModal.name}</h5>
              <button onClick={() => setAssociationModal(null)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {associationModal.response.status ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                  <i className="bi bi-check-circle-fill"></i>
                  This user can be safely deleted.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-700 rounded-lg mb-3">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    This user has associations and cannot be deleted:
                  </div>
                  {associationModal.response.data?.associations?.map((assoc, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg mb-3">
                      <div className="px-3 py-2 bg-gray-50 border-b font-medium text-sm font-semibold">
                        {assoc.type.replace(/_/g, ' ').toUpperCase()} ({assoc.count})
                      </div>
                      <div className="p-3">
                        {assoc.data.slice(0, 5).map((item, j) => (
                          <div key={j} className="text-xs text-gray-600 py-1">
                            {item.name || item.title || item.templateName || item.bookingId || item.transactionId || item.first_name || `ID: ${item._id}`}
                          </div>
                        ))}
                        {assoc.count > 5 && <div className="text-xs text-gray-400">... and {assoc.count - 5} more</div>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button onClick={() => setAssociationModal(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              {associationModal.response.status && (
                <button
                  onClick={handleDeleteUser}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete User'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {imageModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Update Profile Image</h3>
                <p className="text-[11px] text-slate-500">Upload a new image for {selectedUserForImage?.first_name}</p>
              </div>
              <button type="button" onClick={() => setImageModalOpen(false)} className="h-8 w-8 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                <i className="bi bi-x"></i>
              </button>
            </div>
            <form onSubmit={handleImageUpload} className="px-4 py-4 space-y-4">
              <div className="flex flex-col items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <i className="bi bi-person-fill text-slate-400 text-4xl"></i>
                  )}
                </div>
                <label className="mt-3 cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-sm">
                  <i className="bi bi-upload mr-1.5"></i>
                  Choose File
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setImageModalOpen(false)} className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-xs font-semibold">
                  Cancel
                </button>
                <button type="submit" disabled={imageUploading || !newImageFile} className="px-4 py-2 bg-[#009ef7] text-white hover:bg-[#009ef7]/90 disabled:opacity-50 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                  {imageUploading ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
